/**
 * UserManagementListPage — mirrors Angular UserManagementListComponent.
 * All business logic from USER_MANAGEMENT_MODULE.md §2.
 */
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Loader2, Filter, Trash2, Download, Mail, ArrowLeftRight } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Tooltip } from '@/components/ui/Tooltip'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useUserRole } from '@/hooks/useUserRole'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { useAppDispatch } from '@/app/hooks'
import {
  rollbackStaffImpersonationAttempt,
  staffImpersonateAndRedirect,
} from '@/lib/auth/staffImpersonation'
import { useUserManagementList } from '../hooks/useUserManagementList'
import { UserTable } from '../components/UserTable'
import { UserGrid } from '../components/UserGrid'
import { UserFilters } from '../components/UserFilters'
import { EmailBlastModal } from '../components/EmailBlastModal'
import { SwapContactsModal } from '../components/SwapContactsModal'
import {
  deleteUser,
  disableUser,
  bulkDeleteUsers,
  getUserExportUrl,
} from '../services/userManagementApi'
import type { UserListItem, DeleteState, PaginationControlsProps, UserFilters as UserFiltersType } from '../types'
import {
  USER_MANAGEMENT_POLICIES,
  PER_PAGE_OPTIONS,
  PROTECTED_ROLES_DISPLAY,
  DEFAULT_FILTERS,
} from '../constants'
import { stripNonAscii } from '../utils/userManagementFormatters'
import { countActiveUserFilters } from '../utils/activeFilterCount'
import { LIBERTY_FOCUS_INPUT } from '../utils/libertyAdminUi'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'

// ─── Pagination ────────────────────────────────────────────────────────────────
function PageSummary({
  currentPage,
  totalPages,
  totalRecords,
  onChangePage,
  testidPrefix,
}: {
  currentPage: number
  totalPages: number
  totalRecords: number
  onChangePage: (page: number) => void
  testidPrefix: string
}) {
  const [inputValue, setInputValue] = useState(String(currentPage))

  useEffect(() => {
    setInputValue(String(currentPage))
  }, [currentPage])

  const handleBlurOrEnter = () => {
    const val = Number(inputValue)
    if (!isNaN(val) && val >= 1 && val <= totalPages) {
      if (val !== currentPage) {
        onChangePage(val)
      }
    } else {
      setInputValue(String(currentPage))
    }
  }

  return (
    <>
      <span>Page</span>{' '}
      {totalPages > 1 ? (
        <input
          type="number"
          min={1}
          max={totalPages}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlurOrEnter}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleBlurOrEnter()
            }
          }}
          className="mx-1.5 inline-block h-7 w-14 rounded-md border border-slate-300 bg-white px-1 text-center text-xs font-semibold text-slate-900 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-1 focus:ring-[rgb(21_151_255)]"
          aria-label="Go to page"
          data-testid={`${testidPrefix}-goto-page-input`}
        />
      ) : (
        <span className="font-bold text-slate-900">{currentPage}</span>
      )}{' '}
      of <span className="font-bold text-slate-900">{totalPages}</span>
      <span className="mx-2 text-slate-300">|</span>
      Total <span className="font-bold text-slate-900">{totalRecords}</span> Records
    </>
  )
}

function PaginationControls({
  currentPage,
  totalPages,
  onChangePage,
  testidPrefix,
}: PaginationControlsProps) {
  const btnBase =
    'min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 enabled:hover:bg-slate-50 enabled:hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all'

  return (
    <div className="flex items-center gap-1.5" data-testid={`${testidPrefix}-pagination`}>
      <Tooltip content="First">
        <button
          className={btnBase}
          onClick={() => onChangePage(1)}
          disabled={currentPage === 1}
          type="button"
          aria-label="First page"
          data-testid={`${testidPrefix}-pagination-first-btn`}
        >&laquo;</button>
      </Tooltip>
      <Tooltip content="Previous">
        <button
          className={btnBase}
          onClick={() => onChangePage(currentPage - 1)}
          disabled={currentPage === 1}
          type="button"
          aria-label="Previous page"
          data-testid={`${testidPrefix}-pagination-prev-btn`}
        >&lsaquo;</button>
      </Tooltip>
      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
        let p: number
        if (totalPages <= 5) p = i + 1
        else if (currentPage <= 3) p = i + 1
        else if (currentPage >= totalPages - 2) p = totalPages - 4 + i
        else p = currentPage - 2 + i
        const isActive = currentPage === p
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChangePage(p)}
            data-testid={`${testidPrefix}-pagination-page-${p}-btn`}
            className={`min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
              isActive
                ? 'bg-[rgb(21_151_255)] text-white shadow-md border border-[rgb(21_151_255)]'
                : 'bg-white text-slate-600 border border-slate-200 enabled:hover:bg-slate-50'
            }`}
          >
            {p}
          </button>
        )
      })}
      <Tooltip content="Next">
        <button
          className={btnBase}
          onClick={() => onChangePage(currentPage + 1)}
          disabled={currentPage === totalPages}
          type="button"
          aria-label="Next page"
          data-testid={`${testidPrefix}-pagination-next-btn`}
        >&rsaquo;</button>
      </Tooltip>
      <Tooltip content="Last">
        <button
          className={btnBase}
          onClick={() => onChangePage(totalPages)}
          disabled={currentPage === totalPages}
          type="button"
          aria-label="Last page"
          data-testid={`${testidPrefix}-pagination-last-btn`}
        >&raquo;</button>
      </Tooltip>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export function UserManagementListPage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()
  const { hasPolicy, isAdmin, isCompanyRole, isPropertyRole, companyId, propertyId, companyName, propertyName } = useUserRole()

  const {
    users,
    loading,
    sorting,
    error,
    pagination,
    filters,
    initialize,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  } = useUserManagementList()

  // Permissions
  const canAddUser = hasPolicy([USER_MANAGEMENT_POLICIES.ADD_USER])
  const canMultiDelete = hasPolicy([USER_MANAGEMENT_POLICIES.MULTI_DELETE])
  const canEmailBlast = hasPolicy([USER_MANAGEMENT_POLICIES.EMAIL_BLAST])
  const canExport = hasPolicy([USER_MANAGEMENT_POLICIES.EXPORT])

  // UI state
  const [isListView, setIsListView] = useState(!isMobile)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [processing, setProcessing] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Delete dialog
  const [deleteState, setDeleteState] = useState<DeleteState>({
    open: false,
    step: 'choose',
    user: null,
  })

  // Multi-delete confirm
  const [multiDeleteOpen, setMultiDeleteOpen] = useState(false)

  // Email blast: scope confirm (Angular parity — always confirms before modal opens) + modal
  const [emailBlastConfirmOpen, setEmailBlastConfirmOpen] = useState(false)
  const [emailBlastOpen, setEmailBlastOpen] = useState(false)

  // Swap contacts modal
  const [swapContactsOpen, setSwapContactsOpen] = useState(false)

  // Search (debounced) — refs avoid re-running the timer when `applyFilters` / filters
  // identity changes, and let us skip API calls when the value matches the applied filter.
  const [searchTerm, setSearchTerm] = useState(() => filters.username ?? '')
  const appliedUsernameRef = useRef<string>(stripNonAscii(filters.username ?? ''))
  const applyFiltersRef = useRef(applyFilters)
  applyFiltersRef.current = applyFilters
  appliedUsernameRef.current = stripNonAscii(filters.username ?? '')

  const getRoleOverrides = (): Partial<UserFiltersType> | undefined => {
    const overrides: Partial<UserFiltersType> = {}
    if (isPropertyRole && propertyId) {
      overrides.property_id = String(propertyId)
      if (propertyName) overrides.property_name = propertyName
    }
    if (isCompanyRole && companyId) {
      overrides.company_id = String(companyId)
      if (companyName) overrides.company_name = companyName
    }
    if (isCompanyRole || isPropertyRole) {
      overrides.organization_type = 'PMC'
    }
    return Object.keys(overrides).length > 0 ? overrides : undefined
  }

  const filterBaseline = useMemo(
    (): UserFiltersType => ({ ...DEFAULT_FILTERS, ...(getRoleOverrides() ?? {}) }),
    // Role scope is stable for the session; match initialize/reset overrides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPropertyRole, isCompanyRole, propertyId, propertyName, companyId, companyName]
  )

  useEffect(() => {
    // Angular getAccessiblePermission(): pre-filter by property/company based on current role
    initialize(getRoleOverrides())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setIsListView(!isMobile)
  }, [isMobile])

  const handleMainSearchPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData?.getData('text') ?? ''
    const el = e.currentTarget
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    const next = el.value.slice(0, start) + pasted + el.value.slice(end)
    e.preventDefault()
    setSearchTerm(next)
    const clean = stripNonAscii(next).trim()
    appliedUsernameRef.current = clean
    applyFiltersRef.current({ username: clean || undefined, page: 1 })
  }

  const handleMainSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const clean = stripNonAscii(searchTerm).trim()
      appliedUsernameRef.current = clean
      applyFiltersRef.current({ username: clean || undefined, page: 1 })
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = stripNonAscii(searchTerm).trim()
      const applied = appliedUsernameRef.current
      if (next === applied) return
      applyFiltersRef.current({ username: next || undefined, page: 1 })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // ─── Selection ───────────────────────────────────────────────────────────────

  const handleSelectOne = (userId: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    )
  }

  const handleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds([])
      return
    }
    const selectable = users
      .filter((u) => {
        if (!isAdmin) {
          const isProtected = u.rolename?.some((r) =>
            (PROTECTED_ROLES_DISPLAY as readonly string[]).includes(r.rolename)
          )
          if (isProtected) return false
        }
        return true
      })
      .map((u) => u.user_id)
    setSelectedIds(selectable)
  }

  // ─── Impersonate ──────────────────────────────────────────────────────────────

  const handleImpersonate = async (roleId: string) => {
    try {
      await staffImpersonateAndRedirect(dispatch, roleId)
    } catch (err) {
      rollbackStaffImpersonationAttempt()
      showApiError(err)
    }
  }

  // ─── Delete ───────────────────────────────────────────────────────────────────

  const handleDeleteClick = (user: UserListItem) => {
    setDeleteState({ open: true, step: 'choose', user })
  }

  const handleDeleteChoiceDelete = () => {
    setDeleteState((prev) => ({ ...prev, step: 'confirm-delete' }))
  }

  const handleDeleteChoiceDisable = () => {
    setDeleteState((prev) => ({ ...prev, step: 'confirm-disable' }))
  }

  const handleDeleteConfirmed = async () => {
    if (!deleteState.user) return
    setDeleteState((prev) => ({ ...prev, open: false }))
    setProcessing(true)
    try {
      await deleteUser(deleteState.user.user_id)
      showSuccess('User Action Updated Successfully!')
      refetch()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  const handleDisableConfirmed = async () => {
    if (!deleteState.user) return
    setDeleteState((prev) => ({ ...prev, open: false }))
    setProcessing(true)
    try {
      await disableUser(deleteState.user.user_id)
      showSuccess('User Action Updated Successfully!')
      refetch()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  // ─── Multi-delete ─────────────────────────────────────────────────────────────

  const handleMultiDelete = async () => {
    setMultiDeleteOpen(false)
    setProcessing(true)
    try {
      const msg = await bulkDeleteUsers(selectedIds)
      showSuccess(msg)
      setSelectedIds([])
      refetch()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  // ─── Export ───────────────────────────────────────────────────────────────────

  const handleExport = () => {
    setExporting(true)
    try {
      const url = getUserExportUrl(filters)
      // Trigger download/navigation in the same tab context (do not open a new tab).
      const link = document.createElement('a')
      link.href = url
      link.target = '_self'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } finally {
      // Keep spinner briefly for user feedback while download starts.
      window.setTimeout(() => setExporting(false), 600)
    }
  }

  // ─── Reset ────────────────────────────────────────────────────────────────────

  const handleResetFilters = () => {
    setSearchTerm('')
    appliedUsernameRef.current = ''
    resetFilters(getRoleOverrides())
  }

  const currentPage = pagination.current || 1
  const totalPages = pagination.last || 1

  const activeFiltersCount = countActiveUserFilters(filters, filterBaseline)

  // Angular parity: scope confirm message varies by selection + filter state
  // (user-management-list.component.ts:144-151). `filtersCountForBlast` matches
  // Angular's `getFilterValue()` — ALL non-pagination filter keys including username.
  const filtersCountForBlast = Object.entries(filters).filter(([key, value]) => {
    if (['page', 'perPage', 'sortBy', 'orderBy'].includes(key)) return false
    return value !== undefined && value !== null && value !== ''
  }).length
  const totalUsers = pagination.total ?? 0
  const emailBlastScopeMessage =
    selectedIds.length > 0
      ? `There is a filter in list using user selection, Blast email will send to ${selectedIds.length} user`
      : filtersCountForBlast === 0
        ? `There is no filter in list, Blast email will send to all users, there is total ${totalUsers} user`
        : `There is a filter in list, Blast email will send to ${totalUsers} user`

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12" data-testid="um-list-page">
      <PageHeader title="USER MANAGEMENT" testIdRoot="um-list-page-header" />

      <div
        data-testid="um-list-main"
        className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8"
      >
        {/* Toolbar */}
        <div
          data-testid="um-list-toolbar"
          className="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          {/* Search */}
          <div
            data-testid="um-list-search-wrap"
            className="relative w-full min-w-0 max-w-lg md:w-1/2"
          >
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search By Name or Email.."
              className={cn(
                'w-full h-10 pl-12 pr-4 bg-white border border-slate-200 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] placeholder:text-slate-400',
                LIBERTY_FOCUS_INPUT
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPaste={handleMainSearchPaste}
              onKeyDown={handleMainSearchKeyDown}
              data-testid="um-list-search-input"
            />
          </div>

          {/* Actions */}
          <div
            data-testid="um-list-actions"
            className="flex w-full items-center gap-2 sm:gap-3 md:w-auto flex-wrap"
          >
            {/* Multi-Delete */}
            {canMultiDelete && (
              <button
                type="button"
                onClick={() => setMultiDeleteOpen(true)}
                disabled={selectedIds.length === 0}
                data-testid="um-list-multi-delete-btn"
                className={cn(primaryEntityActionButtonClass, 'gap-2')}
              >
                <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                <span>Multi-Delete{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}</span>
              </button>
            )}

            {/* Email Blast */}
            {canEmailBlast && (
              <button
                type="button"
                onClick={() => setEmailBlastConfirmOpen(true)}
                disabled={processing || users.length === 0}
                data-testid="um-list-email-blast-btn"
                className={cn(primaryEntityActionButtonClass, 'gap-2')}
              >
                <Mail className="h-4 w-4 shrink-0" />
                <span>Email Blast</span>
              </button>
            )}

            {/* Swap Contacts */}
            <button
              type="button"
              onClick={() => setSwapContactsOpen(true)}
              data-testid="um-list-swap-contacts-btn"
              className={cn(primaryEntityActionButtonClass, 'gap-2')}
            >
              <ArrowLeftRight className="h-4 w-4 shrink-0" />
              <span>Swap Contacts</span>
            </button>

            {/* Export */}
            {canExport && users.length > 0 && (
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                data-testid="um-list-export-btn"
                className={cn(primaryEntityActionButtonClass, 'gap-2')}
              >
                {exporting ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Download className="h-4 w-4 shrink-0" />}
                <span>{exporting ? 'Exporting...' : 'Export'}</span>
              </button>
            )}

            {/* Add User */}
            {canAddUser && (
              <button
                type="button"
                onClick={() => navigate('/user-management/add')}
                data-testid="um-list-add-user-btn"
                className={cn(primaryEntityActionButtonClass, 'gap-2')}
              >
                <Plus className="h-4 w-4 shrink-0" />
                <span>Add User</span>
              </button>
            )}

            {/* Filters toggle — to the right of Add User */}
            <button
              type="button"
              onClick={() => setIsFilterOpen(true)}
              data-testid="um-list-filters-toggle-btn"
              className={cn(
                secondaryEntityActionButtonClass,
                'relative flex-1 md:flex-none gap-2 font-semibold text-slate-700 whitespace-nowrap',
                activeFiltersCount > 0 && 'border-[rgb(21_151_255)] text-[rgb(21_151_255)]'
              )}
            >
              <Filter className="h-4 w-4 shrink-0" aria-hidden />
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 bg-[rgb(21_151_255)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm leading-none"
                  aria-label={`${activeFiltersCount} filters applied`}
                  data-testid="um-list-filters-count-badge"
                >
                  {activeFiltersCount > 99 ? '99+' : activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters Drawer */}
        <UserFilters
          filters={filters}
          onFiltersChange={(f) => applyFilters(f)}
          onReset={handleResetFilters}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        {/* Results Container */}
        <div
          data-testid="um-list-results-container"
          className="relative flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          {error && !loading && (
            <div
              data-testid="um-list-error"
              className="m-4 rounded-lg bg-red-50 p-4 text-center text-red-600 sm:p-8"
            >
              {error}
            </div>
          )}

          {!error && (
            <>
              {/* Header Pagination Bar */}
              <div
                data-testid="um-list-pagination-header-bar"
                className={cn(
                  'flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 px-3 py-2.5 sm:gap-3 sm:px-6 sm:py-3 lg:flex-row lg:items-center lg:justify-between',
                  loading && 'pointer-events-none',
                )}
              >
                <div
                  data-testid="um-list-pagination-header-summary"
                  className="shrink-0 text-center text-xs font-medium text-slate-500 sm:text-sm lg:max-w-md lg:text-left"
                >
                  <PageSummary
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={pagination.total}
                    onChangePage={changePage}
                    testidPrefix="um-list-top"
                  />
                </div>
                <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-end sm:gap-x-2">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChangePage={changePage}
                    testidPrefix="um-list-top"
                  />
                </div>
              </div>

              {/* Data Table / Grid */}
              <div data-testid="um-list-data-scroll" className="min-w-0">
                <div data-testid="um-list-data-container" className="min-w-full">
                  {sorting && (
                    <div
                      data-testid="um-list-sorting-indicator"
                      className="flex items-center justify-center gap-2 bg-sky-50/80 px-4 py-2 text-xs font-medium text-[rgb(21_151_255)] border-b border-sky-100"
                    >
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      <span>Sorting...</span>
                    </div>
                  )}
                  {loading ? (
                    <div className="flex justify-center items-center py-16">
                      <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
                    </div>
                  ) : isListView ? (
                    <UserTable
                      users={users}
                      onSort={sort}
                      sortBy={filters.sortBy}
                      orderBy={filters.orderBy}
                      sorting={sorting}
                      selectedIds={selectedIds}
                      onSelectOne={handleSelectOne}
                      onSelectAll={handleSelectAll}
                      onImpersonate={handleImpersonate}
                      onDelete={handleDeleteClick}
                    />
                  ) : (
                    <UserGrid
                      users={users}
                      selectedIds={selectedIds}
                      onSelectOne={handleSelectOne}
                      onSelectAll={handleSelectAll}
                      onImpersonate={handleImpersonate}
                      onDelete={handleDeleteClick}
                    />
                  )}
                </div>
              </div>

              {/* Footer Pagination Bar */}
              <div
                data-testid="um-list-pagination-footer-bar"
                className={cn(
                  'flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-3 py-2.5 sm:gap-3 sm:px-6 sm:py-3 lg:flex-row lg:items-center lg:justify-between',
                  loading && 'pointer-events-none',
                )}
              >
                <div
                  data-testid="um-list-pagination-footer-summary"
                  className="shrink-0 text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm lg:max-w-md"
                >
                  <PageSummary
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={pagination.total}
                    onChangePage={changePage}
                    testidPrefix="um-list-bottom"
                  />
                </div>
                <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-end sm:gap-x-2 lg:ml-auto lg:flex-1">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChangePage={changePage}
                    testidPrefix="um-list-bottom"
                  />
                  <div className="flex shrink-0 items-center justify-center gap-1.5 sm:justify-end sm:gap-2 sm:pl-0 sm:ml-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rows</span>
                    <select
                      className="min-w-0 rounded-lg border border-slate-200 bg-white p-1.5 text-xs text-slate-700 sm:p-2 sm:text-sm focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
                      value={filters.perPage ?? 15}
                      onChange={(e) => changePerPage(Number(e.target.value))}
                      data-testid="um-list-bottom-per-page-select"
                    >
                      {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ─── Delete: Choose action dialog ─── */}
      <ConfirmDialog
        isOpen={deleteState.open && deleteState.step === 'choose'}
        title="Confirm"
        message="Do you want to Delete or Disable the user? Disable, if you are moving a property to be managed by a different company, and you want to keep the user, but need to update their role. Otherwise, delete to remove them from the system."
        primaryLabel="Delete"
        onPrimary={handleDeleteChoiceDelete}
        neutralLabel="Disable"
        onNeutral={handleDeleteChoiceDisable}
        secondaryLabel=""
        onSecondary={() => {}}
        onDismiss={() => setDeleteState({ open: false, step: 'choose', user: null })}
        variant="warning"
        primaryFirst
        overlayDataTestId="um-list-delete-choose-overlay"
        primaryButtonDataTestId="um-list-delete-choose-delete-btn"
        closeButtonDataTestId="um-list-delete-choose-close-btn"
      />

      {/* ─── Delete: Confirm delete ─── */}
      <ConfirmDialog
        isOpen={deleteState.open && deleteState.step === 'confirm-delete'}
        title="Delete User?"
        message="Are you sure want to Delete User?"
        primaryLabel="Delete"
        onPrimary={handleDeleteConfirmed}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteState({ open: false, step: 'choose', user: null })}
        variant="error"
        primaryFirst
        overlayDataTestId="um-list-confirm-delete-overlay"
        primaryButtonDataTestId="um-list-confirm-delete-btn"
        secondaryButtonDataTestId="um-list-confirm-delete-cancel-btn"
        closeButtonDataTestId="um-list-confirm-delete-close-btn"
      />

      {/* ─── Delete: Confirm disable ─── */}
      <ConfirmDialog
        isOpen={deleteState.open && deleteState.step === 'confirm-disable'}
        title="Disable User?"
        message="Are you sure want to Disable User"
        primaryLabel="Disable"
        onPrimary={handleDisableConfirmed}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteState({ open: false, step: 'choose', user: null })}
        variant="warning"
        primaryFirst
        overlayDataTestId="um-list-confirm-disable-overlay"
        primaryButtonDataTestId="um-list-confirm-disable-btn"
        secondaryButtonDataTestId="um-list-confirm-disable-cancel-btn"
        closeButtonDataTestId="um-list-confirm-disable-close-btn"
      />

      {/* ─── Email Blast: scope confirm (Angular parity, always fires before modal) ─── */}
      <ConfirmDialog
        isOpen={emailBlastConfirmOpen}
        title="Email Blast"
        message={emailBlastScopeMessage}
        primaryLabel="Confirm"
        onPrimary={() => {
          setEmailBlastConfirmOpen(false)
          setEmailBlastOpen(true)
        }}
        secondaryLabel="Cancel"
        onSecondary={() => setEmailBlastConfirmOpen(false)}
        onDismiss={() => setEmailBlastConfirmOpen(false)}
        primaryFirst
        overlayDataTestId="um-list-email-blast-confirm-overlay"
        primaryButtonDataTestId="um-list-email-blast-confirm-btn"
        secondaryButtonDataTestId="um-list-email-blast-confirm-cancel-btn"
        closeButtonDataTestId="um-list-email-blast-confirm-close-btn"
      />

      {/* ─── Email Blast modal ─── */}
      <EmailBlastModal
        isOpen={emailBlastOpen}
        onClose={() => setEmailBlastOpen(false)}
        onSuccess={refetch}
        selectedIds={selectedIds}
        totalUsers={pagination.total ?? 0}
        filters={filters}
      />

      {/* ─── Swap Contacts modal ─── */}
      <SwapContactsModal
        isOpen={swapContactsOpen}
        onClose={() => setSwapContactsOpen(false)}
        onSuccess={refetch}
      />

      {/* ─── Multi-delete confirm ─── */}
      <ConfirmDialog
        isOpen={multiDeleteOpen}
        title="Confirm Bulk Delete"
        message={`Are You Sure To Delete selected ${selectedIds.length} users?`}
        primaryLabel="Delete"
        onPrimary={handleMultiDelete}
        secondaryLabel="Cancel"
        onSecondary={() => setMultiDeleteOpen(false)}
        variant="error"
        primaryFirst
        overlayDataTestId="um-list-bulk-delete-overlay"
        primaryButtonDataTestId="um-list-bulk-delete-confirm-btn"
        secondaryButtonDataTestId="um-list-bulk-delete-cancel-btn"
        closeButtonDataTestId="um-list-bulk-delete-close-btn"
      />
    </div>
  )
}
